/* eslint-disable no-unused-vars */
import React, { useEffect, useContext } from 'react';
import {
  Box,
  Container,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import {
  SearchRounded,
} from '@mui/icons-material';
import {
  Link, useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Elements from '../../../Elements';
import Colors from '../../../../Configs/colors';
import ROUTES from '../../../../Configs/routes';
import { getToken, getUserData } from '../../../../Utils/storages';
import {
  getAuction, getCart, getFixedPrice,
} from '../../../../Redux/Models/market';
import Images from '../../../../Configs/images';
import MarketIcons from '../../../../Configs/marketIcons';
import Fragments from '../../../Fragments';
import AlertContext from '../../../../Context/AlertContext';
import { formatNumToCurrency } from '../../../../Utils/helpers';
import { getCategories, getConditions } from '../../../../Redux/Models/master';
import { getWallet } from '../../../../Redux/Models/user';

export default function Market({
  classes, data, isLoading, forSale, paidItem, auctions, fixedPrice, cart,
}) {
  const userData = getUserData();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [param, setParam] = useSearchParams();
  const { openAlert } = useContext(AlertContext);
  const token = getToken();
  const { t } = useTranslation();

  const mainMenu = [
    {
      title: t('marketPage.orders'),
      icon: MarketIcons.Orders,
      url: !userData.subscription ? ROUTES.subscription() : 'my-orders',
    },
    {
      title: t('marketPage.saleItem'),
      icon: MarketIcons.SaleItem,
      url: !userData.subscription ? ROUTES.subscription() : 'sale-item',
    },
    // {
    //   title: 'Track shipping',
    //   icon: MarketIcons.TrackShipping,
    // },
    {
      title: t('marketPage.reqWine'),
      icon: MarketIcons.RequestWine,
      url: !userData.subscription ? ROUTES.subscription() : ROUTES.requestItem(),
    },
    {
      title: t('marketPage.myBid'),
      icon: MarketIcons.MyBid,
      url: !userData.subscription ? ROUTES.subscription() : 'bid',
    },
    {
      title: t('marketPage.watchlist'),
      icon: MarketIcons.WatchList,
      url: !userData.subscription ? ROUTES.subscription() : 'watchlist',
    },
    // {
    //   title: 'Bid reminder',
    //   icon: MarketIcons.BidReminder,
    // },
    {
      title: t('marketPage.help'),
      icon: MarketIcons.HelpCenter,
      url: ROUTES.helpCenter(),
    },
  ];

  const secondaryMenu = [
    {
      title: t('marketPage.all'),
      icon: MarketIcons.All,
      sortprice: '',
    },
    {
      title: t('marketPage.cheapest'),
      icon: MarketIcons.Cheapest,
      sortprice: 'Lowest',
    },
    {
      title: t('marketPage.expensive'),
      icon: MarketIcons.Expensive,
      sortprice: 'Highest',
    },
  ];

  const { newRequestItem } = useSelector((state) => state.request);
  const { categories, conditions } = useSelector((state) => state.master);

  // const premiumUser = userData.subscription === 'Premium Plan';

  useEffect(() => {
    if (forSale) {
      openAlert({
        open: true,
        message: 'Item posted on the market',
        severity: 'success',
      });
    }

    if (newRequestItem) {
      openAlert({
        open: true,
        message: 'Request item in process',
        severity: 'success',
      });
    }

    if (paidItem) {
      openAlert({
        open: true,
        message: 'Payment successful. The item has become yours.',
        severity: 'success',
      });
    }
  }, [forSale, newRequestItem, paidItem]);

  useEffect(() => {
    dispatch(getAuction(userData.id));
    dispatch(getFixedPrice(userData.id));
    dispatch(getCart(userData.id));
    dispatch(getCategories());
    dispatch(getConditions());
    dispatch(getWallet(userData.id));
  }, []);

  return (
    <Elements.PageBase noNav={!token}>
      <Box className={classes.root}>
        <Box bgcolor={Colors.primary} py={2}>
          <Container>
            <Box display="flex" alignItems="center" gap={2}>
              <TextField
                fullWidth
                size="small"
                onChange={({ target }) => setParam({ keyword: target.value })}
                value={param.get('keyword') || ''}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (<IconButton sx={{ p: 0 }} component={Link} to={`${ROUTES.market()}/search?keyword=${param.get('keyword') || ''}`}><SearchRounded className="mute" /></IconButton>),
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    navigate(`${ROUTES.market()}/search?keyword=${param.get('keyword') || ''}`);
                  }
                }}
                placeholder={t('marketPage.search')}
                sx={{
                  bgcolor: 'white',
                  borderRadius: 2,
                  overflow: 'hidden',
                  outline: 'none',
                }}
              />
              {
                cart && cart.length > 0 ? (
                  <Box position="relative">
                    <IconButton
                      sx={{ p: 0 }}
                      onClick={() => navigate(ROUTES.cart())}
                    >
                      <img alt="cart" src={Images.Market} width="24" height="24" />
                    </IconButton>
                    <Box
                      className="s-6 weight-700 primary-green flex flex-center align-items-center"
                      borderRadius="50%"
                      bgcolor="#fff"
                      width="10px"
                      height="10px"
                      position="absolute"
                      top="0"
                      right="0"
                    >
                      {cart.length}
                    </Box>
                  </Box>
                )
                  : (
                    <IconButton
                      disabled={!userData.subscription}
                      sx={{ p: 0 }}
                      onClick={() => navigate(ROUTES.cart())}
                    >
                      <img alt="cart" src={Images.Market} width="24" height="24" />
                    </IconButton>
                  )
              }
            </Box>
            <Box mt={2} bgcolor="#fff" p={1.5} borderRadius="5px">
              <Elements.Balance />
            </Box>
          </Container>
        </Box>

        <Container sx={{ my: 3 }}>
          <Grid container spacing={1.5}>
            {mainMenu.map((menu) => (
              <Grid item xs={3} key={menu.title}>
                <Box
                  gap={0.5}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  as={Link}
                  to={menu.url}
                  className="link"
                >
                  <img alt="market menu" src={menu.icon} width="28" height="28" />
                  <Box color="#404852" className="weight-600 s-10">{menu.title}</Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Box>
          <Fragments.AdsCarousel />
        </Box>

        <Container sx={{ my: 3 }}>
          <Grid container spacing={3}>
            {secondaryMenu.map((menu) => (
              <Grid item xs={3} key={menu.title}>
                <Box
                  gap={0.5}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  as="a"
                  href={`${ROUTES.market()}/search?sortprice=${menu.sortprice}`}
                  className="link"
                >
                  <img alt="market menu" src={menu.icon} width="28" height="28" />
                  <Box color="#404852" className="weight-600 s-10">{menu.title}</Box>
                </Box>
              </Grid>
            ))}
            {categories.map((category) => (
              <Grid item xs={3} key={category.name}>
                <Box
                  gap={0.5}
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  as="a"
                  href={`${ROUTES.market()}/search?category=${category.id}`}
                  className="link"
                >
                  <img alt="market menu" src={category.image_url} width="28" height="28" />
                  <Box color="#404852" className="weight-600 s-10">{category.name}</Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>

        <Container>
          <Box display="flex" justifyContent="space-between" alignItems="flex-end">
            <Box>
              <Box color="#2E3C40" className="s-14 weight-700">{t('marketPage.auctionTitle')}</Box>
              <Box className="s-10" mt="2px">{t('marketPage.auctionDesc')}</Box>
            </Box>
            <Box color={Colors.primary} className="s-12 weight-600 link" as="a" href={`${ROUTES.market()}/search?type=Auction`}>{t('marketPage.seeAll')}</Box>
          </Box>
          {auctions.map((el, index) => {
            const item = el.item || el.box;
            return (
              <Box display="flex" gap={1.5} mt={1.5} mb={2} onClick={() => navigate(`${ROUTES.market()}/search/${item.id}`)} key={index}>
                <img
                  width="80px"
                  height="80px"
                  className="rounded cover"
                  src={item.photo}
                  alt="baco"
                />
                <Box flex={1}>
                  <Box className="weight-600">{item.name}</Box>
                  <Box display="flex" className="s-10 mute" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <div>{item.category_name || '-'}</div>
                    <div>
                      ・
                      {item.condition_name || '-'}
                    </div>
                    <div>
                      ・
                      {item?.volume || '0 mL'}
                    </div>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Box className="s-16 weight-700">
                      {formatNumToCurrency(Number(item.price))}
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <Box>
                        {t('marketPage.qty')}
                        {' '}
                        1
                      </Box>
                      <img src={Images.CheckRed} alt="check-icon" />
                    </Box>
                  </Box>
                  {item.sale_type === 'Bid Price'
                    && (
                    <Box className="s-12" mt={0.5}>
                      {item.bidder_count}
                      {' '}
                      bids・
                      {moment(item.end_date).format('DD MMM YYYY (HH.mm)')}
                    </Box>
                    )}
                </Box>
              </Box>
            );
          })}

          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mt={3}>
            <Box>
              <Box color="#2E3C40" className="s-14 weight-700">{t('marketPage.fixedTitle')}</Box>
              <Box className="s-10" mt="2px">{t('marketPage.fixedDesc')}</Box>
            </Box>
            <Box color={Colors.primary} className="s-12 weight-600 link" as="a" href={`${ROUTES.market()}/search?type=Fixed Price`}>{t('marketPage.seeAll')}</Box>
          </Box>
          {fixedPrice.map((el, index) => {
            const item = el.item || el.box;
            return (
              <Box display="flex" gap={1.5} mt={1.5} mb={2} onClick={() => navigate(`${ROUTES.market()}/search/${item.id}`)} key={index}>
                <img
                  width="80px"
                  height="80px"
                  className="rounded cover"
                  src={item.photo}
                  alt="baco"
                />
                <Box flex={1}>
                  <Box className="weight-600">{item.name}</Box>
                  <Box display="flex" className="s-10 mute" gap={0.5} flexWrap="wrap" alignItems="center" mt={0.25}>
                    <div>{item.category_name || '-'}</div>
                    <div>
                      ・
                      {item.condition_name || '-'}
                    </div>
                    <div>
                      ・
                      {item?.volume || '0 mL'}
                    </div>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Box className="s-16 weight-700">
                      {formatNumToCurrency(Number(item.price))}
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <img src={Images.CheckRed} alt="check-icon" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Container>

      </Box>

      <Elements.Footer />
    </Elements.PageBase>
  );
}
